import { isWebDataSuccess } from '@cobliteam/api-lib';
import type { GeofenceCategory } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { selectAddress } from '@cobliteam/store';
import Typography from '@cobliteam/ui/components/Typography';
import { format } from '@cobliteam/utils/date';
import { formatDurationShort } from '@cobliteam/utils/time';
import type React from 'react';
import { useSelector } from 'react-redux';
import type { ProcessedStop, ProcessedStopRouteStep } from '../../store';
import {
  TrackingPopUp,
  TrackingPopUpContent,
  TrackingPopUpFooter,
  TrackingPopUpHeader,
  TrackingPopUpHeaderMain,
  TrackingPopUpInfoRow,
  TrackingPopUpInfoRowText,
  TrackingPopUpSkeleton,
  TrackingPopUpTitle,
} from '../Tracking/components/Common/TrackingPopUp';
import { StopIconComponent } from '../Tracking/components/Map/MapIcons/StopIcon/StopIcon';
import GeofenceBadge from './GeofenceBadge';
import IdleEngineContent from './IdleEngineContent';
import * as styles from './styles';

export interface StopPopupProps {
  stop: ProcessedStop | ProcessedStopRouteStep;
  idleTimeHighlighted?: boolean;
  filteredDuration?: number;
  children?: React.ReactNode;
}

export const StopPopup = ({ stop, children, idleTimeHighlighted, filteredDuration }: StopPopupProps) => {
  const { t } = useTranslation('tracking');
  const { isAccessAllowed } = usePermissions();
  const isAllowedViewGeofences = isAccessAllowed('view:places-of-interest');
  const addressWebData = useSelector(selectAddress);

  const stopNumber = stop.index + 1;
  const address = isWebDataSuccess(addressWebData) ? addressWebData.data : <TrackingPopUpSkeleton />;
  const dateText = `${format(stop.startTime, "dd/MM/yyyy 'de' HH:mm")} às ${format(stop.endTime, 'HH:mm')}`;

  const stopDuration = stop.endTime - stop.startTime;
  const hasGeofence = stop.geofences.length > 0;
  const selectedGeofence = stop.geofences[0];
  const stopIconOptions = {
    hasGeofence,
    isAllowedToViewPlacesOfInterest: isAllowedViewGeofences,
  };

  return (
    <TrackingPopUp>
      <TrackingPopUpHeader>
        <TrackingPopUpHeaderMain>
          <StopIconComponent index={stopNumber - 1} options={stopIconOptions} />
          <TrackingPopUpTitle>{t('stop_number', { number: stopNumber })}</TrackingPopUpTitle>
        </TrackingPopUpHeaderMain>
        {hasGeofence && <GeofenceBadge category={(selectedGeofence.category as GeofenceCategory) || 'default'} />}
      </TrackingPopUpHeader>
      <TrackingPopUpContent>
        <TrackingPopUpInfoRowText title={t('date')}>{dateText}</TrackingPopUpInfoRowText>
        {hasGeofence && <TrackingPopUpInfoRowText title={t('place_of_interest')}>{selectedGeofence.name}</TrackingPopUpInfoRowText>}
        <TrackingPopUpInfoRowText title={t('address')}>{address}</TrackingPopUpInfoRowText>
        <TrackingPopUpInfoRowText title={t('driver')}>{stop.driverName || t('no_drivers')}</TrackingPopUpInfoRowText>
        {stop.idleOnInMillis > 0 && (
          <TrackingPopUpInfoRow title={t('idle_engine')}>
            <IdleEngineContent stop={stop} idleTimeHighlighted={idleTimeHighlighted}>
              {formatDurationShort(stop.idleOnInMillis, true)}
            </IdleEngineContent>
          </TrackingPopUpInfoRow>
        )}
        <TrackingPopUpInfoRowText title={t('duration')}>{formatDurationShort(stopDuration, true) || '---'}</TrackingPopUpInfoRowText>

        {filteredDuration && (
          <div className={styles.filteredHighlight}>
            <Typography variant='description-list-default'>
              {t('you_are_viewing')} <b>{formatDurationShort(filteredDuration)}</b> {t('of_total_stop_time')}
            </Typography>
          </div>
        )}
      </TrackingPopUpContent>
      <TrackingPopUpFooter>{children}</TrackingPopUpFooter>
    </TrackingPopUp>
  );
};
